/**
 * Lightweight <Avatar> primitive (Feature 1 — User filter autocomplete).
 *
 * Rationale for NOT using `@radix-ui/react-avatar`:
 *   - The Slack user-search schema exposes no profile-pic URL (see
 *     `fetchUserSearch` — returns only slackId/username/displayName), so the
 *     image-with-fallback machinery Radix offers buys us nothing.
 *   - Adding a new Radix dep just for the fallback circle would bloat the
 *     bundle. The UserMultiSelect autocomplete renders ONE avatar per row in
 *     a ≤20-row combobox, so a plain span is plenty.
 *
 * API surface: mirrors the subset of shadcn's `<Avatar>` we actually need —
 *   <Avatar><AvatarFallback>AB</AvatarFallback></Avatar>
 * — so future callers can promote this to Radix without touching call sites.
 *
 * Colour: a deterministic hue derived from the initials so the same user
 * always gets the same circle colour across renders. Uses HSL (60 slots),
 * which gives visible variance while staying readable on the neutral shadcn
 * bg tokens.
 */

import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const Avatar = forwardRef<HTMLSpanElement, HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'relative flex h-7 w-7 shrink-0 overflow-hidden rounded-full',
        className,
      )}
      {...props}
    />
  ),
);
Avatar.displayName = 'Avatar';

interface AvatarFallbackProps extends HTMLAttributes<HTMLSpanElement> {
  /** Seed for deterministic hue (usually the username). Falls back to
   *  the child text content when omitted. */
  seed?: string;
}

function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  // 60 evenly-spaced hues across the wheel — plenty of variance for a
  // combobox list while keeping the palette deterministic.
  return Math.abs(h) % 360;
}

const AvatarFallback = forwardRef<HTMLSpanElement, AvatarFallbackProps>(
  ({ className, children, seed, style, ...props }, ref) => {
    const seedStr =
      seed ?? (typeof children === 'string' ? children : '') ?? '';
    const hue = hueFromString(seedStr || 'x');
    return (
      <span
        ref={ref}
        className={cn(
          'flex h-full w-full items-center justify-center rounded-full text-xs font-medium text-white',
          className,
        )}
        style={{
          backgroundColor: `hsl(${hue} 55% 45%)`,
          ...style,
        }}
        {...props}
      >
        {children}
      </span>
    );
  },
);
AvatarFallback.displayName = 'AvatarFallback';

export { Avatar, AvatarFallback };
